/* import __COLOCATED_TEMPLATE__ from './payment-form.hbs'; */
import { action } from '@ember/object';
import { bannerError, callbackError } from 'client-portal/utils/error-handling';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import styles from './payment-form.module.scss';

export default class PaymentLinkPaymentForm extends Component {
  styles = styles;
  @service stripePaymentModeElementService;
  @service currentPractice;
  @service stripe;
  @service store;
  @service router;

  @tracked stripeElement;
  @tracked card;
  @tracked isPersisting = false;

  @action
  initializeStripeElement() {
    this.stripeElement = this.stripePaymentModeElementService.createElement(
      this.stripe.stripe,
      this._stripeElementPaymentOptions()
    );
    this.card = this.store.createRecord('card', { isDefault: false });
  }

  @action
  async persistCardAndPayment() {
    const stripeError = await this._runStripeValidations();
    const cardError = await this._runCardValidations();

    if (stripeError || cardError) return;

    this.isPersisting = true;
    try {
      await this._createCardOnStripe(this.stripeElement);
      await this._createPayment();
      window.location.reload();
    } catch (error) {
      this._handlePaymentFailure(error);
    }
    this.isPersisting = false;
  }

  async _runStripeValidations() {
    let { error } = await this.stripeElement.submit(); // Stripe named it 'submit' but it actually only validates the form

    return error;
  }

  async _runCardValidations() {
    this.card.set('_isDirty', true);

    return !this.card.name?.trim();
  }

  async _createCardOnStripe(elements) {
    const { paymentMethod } = await this.stripe.stripe.createPaymentMethod({
      elements,
      params: {
        /* eslint-disable camelcase */
        billing_details: {
          name: this.card.name,
        },
      },
    });
    const { card } = paymentMethod;

    this.card.setProperties({
      customStripeCustomerId: paymentMethod.customer,
      brand: card.brand,
      last4: card.last4,
      expMonth: card.exp_month,
      expYear: card.exp_year,
      paymentMethodId: paymentMethod.id,
    });
  }

  async _createPayment() {
    let paymentModel = this._setPaymentModel();
    let embed = [];
    // If card is saved as default, we embed the card attrs, otherwise we send the token
    if (this.card.isDefault) {
      embed = ['card'];
    } else {
      paymentModel.set('customCardToken', this.card.paymentMethodId);
    }
    // We use createWithInvoiceItemPayments while we develop PAY-72
    await this.store.adapterFor('payment').createWithInvoiceItemPayments({
      ...paymentModel.serialize({
        adapterOptions: {
          embed,
        },
      }),
      meta: {
        useCredit: false,
        invoiceIdsWithAmounts: this.invoiceIdsWithAmounts,
        paymentLinkUuid: this.args.paymentLink.uuid,
      },
    });
  }

  _handlePaymentFailure(err) {
    callbackError(err, message => {
      let title = 'Payment could not be made';

      if (message.title.match(/Your account cannot currently make charges/)) {
        title = 'Your payment could not be processed';
        message.title = 'Contact your provider for more information.';
      }

      bannerError(err, { title });
      return true;
    });
  }

  _setPaymentModel() {
    return this.store.createRecord('payment', {
      card: this.card,
      amount: this.args.paymentLink.totalPayableAmount,
      maximumAmount: this.args.paymentLink.totalPayableAmount,
      invoice: null,
      amountType: 'total',
    });
  }

  // This get function is temporary in order to make the payment intent work until we have PAY-72
  get invoiceIdsWithAmounts() {
    return this.args.paymentLink.allocatableInvoices.map(invoice => ({
      invoiceId: invoice.invoice_id,
      amount: invoice.payable_amount,
    }));
  }

  _stripeElementPaymentOptions() {
    return {
      amount: this.args.paymentLink.totalPayableAmount,
      currency: this.currentPractice.get('currency'),
      onBehalfOf: this.currentPractice.get('stripeConnectedAccountId'),
      paymentMethodTypes: ['card'],
    };
  }
}
